import { type CollectionName, collectionDefs, type RowOf } from "@/store/collections";
import { EntityStore } from "@/store/EntityStore";
import { createEqualityIndex, type EqualityIndex } from "@/store/indexes";

/** The app-wide entity store — the successor to the PGlite `db` singleton. */
export const store = new EntityStore(collectionDefs);

function indexBy<K extends CollectionName>(name: K, field: keyof RowOf<K> & string): EqualityIndex {
  return createEqualityIndex(store.collection(name), field);
}

/**
 * Incrementally maintained secondary indexes for the hot lookups. Everything else scans its
 * collection — an in-memory pass over even the largest workspace's rows is a few milliseconds.
 * These same indexes serve TanStack live-query predicate evaluation over the collections.
 */
export const idx = {
  itemsByFeed: indexBy("item", "feedId"),
  permissionsByFeed: indexBy("permission", "feedId"),
  permissionsByMembership: indexBy("permission", "workspaceMembershipId"),
  feedsByAliasMembership: indexBy("feed", "aliasMembershipId"),
  commandAliasesByFeed: indexBy("workspaceCommandAlias", "feedId"),
  commandAliasesByMembership: indexBy("workspaceCommandAlias", "workspaceMembershipId"),
  invitationsByMembership: indexBy("directWsInvitation", "workspaceMembershipId"),
  membershipsByWorkspace: indexBy("workspaceMembership", "workspaceId"),
  membershipsByAccount: indexBy("workspaceMembership", "accountId"),
  membershipTagsByMembership: indexBy("workspaceMembershipTag", "workspaceMembershipId"),
  membershipTagsByTag: indexBy("workspaceMembershipTag", "tagId"),
  feedGroupMembershipsByFeed: indexBy("feedGroupMembership", "feedId"),
  feedGroupMembershipsByGroup: indexBy("feedGroupMembership", "groupId"),
  feedPreferencesByFeed: indexBy("feedPreference", "feedId"),
  feedPreferencesByMembership: indexBy("feedPreference", "workspaceMembershipId"),
  transcriptionsByContent: indexBy("transcription", "contentId"),
  filesByContent: indexBy("file", "contentId"),
  linksByContent: indexBy("link", "contentId"),
  audioEncodingsByContent: indexBy("audioEncoding", "contentId"),
  displayArtifactsByContent: indexBy("displayArtifact", "contentId"),
  callRecordsByContent: indexBy("callRecord", "contentId"),
  accountEventsByItem: indexBy("accountEvent", "itemId"),
  accountEventsByFeed: indexBy("accountEvent", "feedId"),
  audioQueueItemsByItem: indexBy("audioQueueItem", "itemId"),
  itemsByGroup: indexBy("item", "groupId"),
  fleetGroupItemsByGroup: indexBy("fleetGroupItem", "fleetGroupId"),
  tmsEventsByMembership: indexBy("tmsEvent", "attributedMembershipId"),
  currentRoutesByWorkspace: indexBy("currentRoute", "workspaceId"),
  feedAcknowledgementsByFeed: indexBy("feedAcknowledgement", "feedId"),
  feedItemAcknowledgementsByItem: indexBy("feedItemAcknowledgement", "feedItemId"),
  anchorAcknowledgementsByAnchor: indexBy("anchorAcknowledgement", "anchorId"),
};

/** Resolves an index lookup to rows. */
export function rowsByIndex<K extends CollectionName>(
  name: K,
  index: EqualityIndex,
  key: string | null | undefined,
): RowOf<K>[] {
  const out: RowOf<K>[] = [];
  for (const rowKey of index.keys(key)) {
    const row = store.get(name, rowKey);
    if (row !== undefined) out.push(row);
  }
  return out;
}

/** Collects the rows of a collection matching a predicate — the JS equivalent of a table scan. */
export function rowsWhere<K extends CollectionName>(name: K, predicate: (row: RowOf<K>) => boolean): RowOf<K>[] {
  const out: RowOf<K>[] = [];
  for (const row of store.getAll(name).values()) {
    if (predicate(row)) out.push(row);
  }
  return out;
}

declare global {
  interface Window {
    /** Console access to the entity store for debugging. */
    __sbStore?: typeof store;
  }
}

if (typeof window !== "undefined") {
  window.__sbStore = store;
}
